import { BsCameraFill } from 'react-icons/bs';
import { MdFiberManualRecord, MdStop } from 'react-icons/md';
import { BiFilterAlt } from 'react-icons/bi';
import { AiOutlineZoomIn, AiOutlineZoomOut } from 'react-icons/ai';
import { IoGameControllerOutline, IoSettingsOutline } from 'react-icons/io5';
import { useEffect, useState } from 'react';
import './screenCapture.css';

// Screen capture utility functions
let mediaRecorder;
let recordedChunks = [];

// Helper function to detect mobile device
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Helper function to share file on mobile
const shareFile = async (blob, fileName, fileType) => {
  try {
    // Create file object
    const file = new File([blob], fileName, { type: fileType });

    // Check if Web Share API is supported
    if (navigator.share && navigator.canShare({ files: [file] })) {
      await navigator.share({
        files: [file],
        title: 'Share AR ' + (fileType.includes('video') ? 'Video' : 'Photo'),
      });
    } else {
      // Fallback to download if sharing is not supported
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error sharing file:', error);
    // Fallback to download if sharing fails
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  }
};

// Fix 1: MP4 first so WhatsApp (which only accepts MP4) gets a compatible file.
// WebM variants are kept as fallback for desktop browsers that don't support MP4 recording.
const getSupportedMimeType = () => {
  const types = [
    'video/mp4;codecs=h264,aac',
    'video/mp4',
    'video/webm;codecs=h264',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ];
  return types.find(type => MediaRecorder.isTypeSupported(type)) || '';
};

// Helper function to draw watermark
const drawWatermark = async (context, canvas) => {
  return new Promise((resolve) => {
    const watermark = new Image();
    watermark.onload = () => {
      // Calculate size (15% of the smaller canvas dimension)
      const size = Math.min(canvas.width, canvas.height) * 0.15;
      const ratio = watermark.width / watermark.height;
      const watermarkWidth = size * ratio;
      const watermarkHeight = size;

      // Position in bottom right corner with padding
      const padding = 30;
      const x = canvas.width - watermarkWidth - padding;
      const y = canvas.height - watermarkHeight - padding;

      // Save the current context state
      context.save();
      
      // Create white glow effect
      context.shadowColor = 'white';
      context.shadowBlur = 3;
      context.shadowOffsetX = 0;
      context.shadowOffsetY = 0;
      
      // First draw: white outline
      context.globalAlpha = 0.4;
      context.drawImage(watermark, x, y, watermarkWidth, watermarkHeight);
      
      // Reset shadow for the main watermark
      context.shadowBlur = 0;
      
      // Second draw: actual watermark
      context.globalAlpha = 0.6;
      context.drawImage(watermark, x, y, watermarkWidth, watermarkHeight);
      
      // Restore the context state
      context.restore();
      resolve();
    };
    watermark.src = '/watermark.png';
  });
};

export const takeScreenshot = async (mindarThree, poseMindarThree = null) => {
  try {
    if (!mindarThree || !mindarThree.video || !mindarThree.renderer) {
      alert('AR view not ready');
      return;
    }

    const {video, renderer} = mindarThree;
    const scene = mindarThree.scene || renderer.scene;
    const camera = mindarThree.camera || renderer.camera;
    const renderCanvas = renderer.domElement;

    // output canvas
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = renderCanvas.width;
    canvas.height = renderCanvas.height;

    // Calculate scaling
    const sx = (video.clientWidth - renderCanvas.clientWidth) / 2 * video.videoWidth / video.clientWidth;
    const sy = (video.clientHeight - renderCanvas.clientHeight) / 2 * video.videoHeight / video.clientHeight;
    const sw = video.videoWidth - sx * 2;
    const sh = video.videoHeight - sy * 2;

    // First, draw the video (flipped)
    context.save();
    context.scale(-1, 1);
    context.drawImage(video, sx, sy, sw, sh, -canvas.width, 0, canvas.width, canvas.height);
    context.restore();

    // Then draw the primary AR overlay (earrings / face tracking)
    renderer.preserveDrawingBuffer = true;
    renderer.render(scene, camera);
    context.drawImage(renderCanvas, 0, 0, canvas.width, canvas.height);
    renderer.preserveDrawingBuffer = false;

    // Composite the pose AR overlay (necklace) when running in Combo mode
    if (poseMindarThree && poseMindarThree.renderer && poseMindarThree.scene && poseMindarThree.camera) {
      const poseRenderCanvas = poseMindarThree.renderer.domElement;
      poseMindarThree.renderer.render(poseMindarThree.scene, poseMindarThree.camera);
      context.drawImage(poseRenderCanvas, 0, 0, canvas.width, canvas.height);
    }

    // Add watermark
    await drawWatermark(context, canvas);

    // Convert to blob for sharing
    canvas.toBlob(async (blob) => {
      if (isMobileDevice()) {
        await shareFile(blob, 'ar-photo.png', 'image/png');
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = 'ar-photo.png';
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      }
    }, 'image/png');
  } catch (err) {
    console.error('Error taking screenshot:', err);
    alert('Could not take screenshot. Please try again.');
  }
};

export const startRecording = async (mindarThree, poseMindarThree = null) => {
  try {
    if (!mindarThree || !mindarThree.video || !mindarThree.renderer) {
      alert('AR view not ready');
      return;
    }

    const mimeType = getSupportedMimeType();
    if (!mimeType) {
      alert('Video recording is not supported on this device');
      return;
    }

    const {video, renderer} = mindarThree;
    const scene = mindarThree.scene || renderer.scene;
    const camera = mindarThree.camera || renderer.camera;
    const renderCanvas = renderer.domElement;

    // Create a canvas for compositing
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = renderCanvas.width;
    canvas.height = renderCanvas.height;

    // Calculate video scaling
    const sx = (video.clientWidth - renderCanvas.clientWidth) / 2 * video.videoWidth / video.clientWidth;
    const sy = (video.clientHeight - renderCanvas.clientHeight) / 2 * video.videoHeight / video.clientHeight;
    const sw = video.videoWidth - sx * 2;
    const sh = video.videoHeight - sy * 2;

    // Load watermark once
    const watermark = new Image();
    await new Promise((resolve) => {
      watermark.onload = resolve;
      watermark.src = '/watermark.png';
    });

    // Capture pose renderer canvas reference once (null when not in Combo mode)
    const poseRenderCanvas = (poseMindarThree && poseMindarThree.renderer) ? poseMindarThree.renderer.domElement : null;

    // Set up animation frame for continuous capture
    const drawFrame = () => {
      // Draw flipped video
      context.save();
      context.scale(-1, 1);
      context.drawImage(video, sx, sy, sw, sh, -canvas.width, 0, canvas.width, canvas.height);
      context.restore();

      // Draw primary AR overlay (earrings / face tracking)
      renderer.render(scene, camera);
      context.drawImage(renderCanvas, 0, 0, canvas.width, canvas.height);

      // Composite pose AR overlay (necklace) when running in Combo mode
      if (poseRenderCanvas && poseMindarThree.scene && poseMindarThree.camera) {
        poseMindarThree.renderer.render(poseMindarThree.scene, poseMindarThree.camera);
        context.drawImage(poseRenderCanvas, 0, 0, canvas.width, canvas.height);
      }

      // Draw watermark
      const size = Math.min(canvas.width, canvas.height) * 0.2;
      const ratio = watermark.width / watermark.height;
      const watermarkWidth = size * ratio;
      const watermarkHeight = size;
      const padding = 20;
      const x = canvas.width - watermarkWidth - padding;
      const y = canvas.height - watermarkHeight - padding;
      context.globalAlpha = 1.0;
      context.drawImage(watermark, x, y, watermarkWidth, watermarkHeight);
      context.globalAlpha = 1.0;

      if (mediaRecorder && mediaRecorder.state === 'recording') {
        requestAnimationFrame(drawFrame);
      }
    };

    // Start recording from the composite canvas
    const stream = canvas.captureStream(30); // 30 FPS
    recordedChunks = [];

    // Configure MediaRecorder with supported options
    const options = {
      mimeType,
      videoBitsPerSecond: 2500000 // 2.5 Mbps
    };

    try {
      mediaRecorder = new MediaRecorder(stream, options);
    } catch (e) {
      console.error('Failed to create MediaRecorder with options', e);
      // Fallback to basic options
      mediaRecorder = new MediaRecorder(stream);
    }

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunks.push(event.data);
      }
    };

    // Fix 2: timeslice of 1 s so iOS Safari flushes data incrementally
    // instead of buffering everything in memory until stop().
    mediaRecorder.start(1000);
    drawFrame();
  } catch (err) {
    console.error('Error starting recording:', err);
    alert('Could not start recording. Please try again.');
  }
};

export const stopRecording = () => {
  if (!mediaRecorder || mediaRecorder.state === 'inactive') return;

  // Fix 3: attach the handler BEFORE calling stop() so we never miss the event.
  mediaRecorder.addEventListener('stop', async () => {
    const mimeType = mediaRecorder.mimeType || 'video/mp4';
    const blob = new Blob(recordedChunks, { type: mimeType });
    recordedChunks = [];

    // Fix 2b: detect a blank/empty recording (iOS canvas.captureStream() failure).
    if (blob.size < 2000) {
      alert('Recording produced no data. Your browser may not support AR canvas recording — try a screenshot instead.');
      return;
    }

    // Fix 4: warn when the file exceeds WhatsApp's 16 MB limit.
    const sizeMB = blob.size / (1024 * 1024);
    if (sizeMB > 16) {
      const go = window.confirm(
        `Video is ${sizeMB.toFixed(1)} MB — WhatsApp's limit is 16 MB and may reject it.\nShare anyway?`
      );
      if (!go) return;
    }

    const extension = mimeType.includes('mp4') ? 'mp4' : 'webm';
    const fileName = `ar-recording.${extension}`;

    if (isMobileDevice()) {
      await shareFile(blob, fileName, mimeType);
    } else {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
    }
  }, { once: true });

  mediaRecorder.stop();
};

export const updateZoom = (rendererInfo, zoomFactor) => {
  const { models } = rendererInfo;
  if (!models) return;

  // zoomFactor: 1 means no change, >1 means zoom in, <1 means zoom out
  Object.entries(models).forEach(([key, model]) => {
    // Skip the occluder
    if (key === 'occluder' || !model || !model.scale) return;

    // Store initial scale if not already stored
    if (!model.userData.initialScale) {
      model.userData.initialScale = {
        x: model.scale.x,
        y: model.scale.y,
        z: model.scale.z
      };
    }

    // Get current scale relative to initial scale
    const currentScale = model.scale.x / model.userData.initialScale.x;
    
    // Apply zoom factor to current scale
    // Limit zoom to reasonable bounds (e.g., 0.1x to 10x of initial size)
    const newScale = Math.max(0.1, Math.min(10, currentScale * zoomFactor));
    
    model.scale.set(
      model.userData.initialScale.x * newScale,
      model.userData.initialScale.y * newScale,
      model.userData.initialScale.z * newScale
    );
  });
};

export const updatePosition = (rendererInfo, direction) => {
  const { plane, models } = rendererInfo;
  if (!plane && !models) return;

  const step = 0.01;

  if (plane) {
    // 2D case - update plane position
    switch(direction) {
      case 'up':
        plane.position.y += step;
        break;
      case 'down':
        plane.position.y -= step;
        break;
      case 'left':
        plane.position.x -= step;
        break;
      case 'right':
        plane.position.x += step;
        break;
      default:
        // No action needed for unknown direction
        break;
    }
  } else if (models) {
    // 3D case - update model positions
    Object.values(models).forEach(model => {
      if (model && model.position) {
        const basePosition = model.userData.basePosition || { x: 0, y: 0, z: 0 };
        switch(direction) {
          case 'up':
            model.position.y = basePosition.y + step;
            break;
          case 'down':
            model.position.y = basePosition.y - step;
            break;
          case 'left':
            model.position.x = basePosition.x - step;
            break;
          case 'right':
            model.position.x = basePosition.x + step;
            break;
          default:
            // No action needed for unknown direction
            break;
        }
      }
    });
  }
};

// Zoom for a specific target. Earring targets always scale both earrings together.
// Necklace uses baseScale stored in userData so the multiplier compounds correctly.
export const updateZoomForTarget = (rendererInfo, zoomFactor, target) => {
  const { models } = rendererInfo;
  if (!models) return;

  const applyMultiplier = (model) => {
    if (!model) return;
    model.userData.zoomMultiplier = Math.max(0.1, Math.min(10, (model.userData.zoomMultiplier ?? 1) * zoomFactor));
    // Necklace: apply immediately since its scale isn't overridden in an animation loop.
    // Earrings: the animation loop reads zoomMultiplier each frame, so no immediate apply needed.
    if (model.userData.baseScale !== undefined) {
      const s = model.userData.baseScale * model.userData.zoomMultiplier;
      model.scale.set(s, s, 1);
    }
  };

  if (target === 'leftEarring' || target === 'rightEarring') {
    applyMultiplier(models.leftEarring);
    applyMultiplier(models.rightEarring);
  } else if (target === 'necklace') {
    applyMultiplier(models.necklace);
  }
};

// Move only the specified target model independently.
export const updatePositionForTarget = (rendererInfo, direction, target) => {
  const { models } = rendererInfo;
  if (!models) return;

  const step = 0.01;
  const model = models[target];
  if (!model?.position) return;

  switch (direction) {
    case 'up':    model.position.y += step; break;
    case 'down':  model.position.y -= step; break;
    case 'left':  model.position.x -= step; break;
    case 'right': model.position.x += step; break;
    default: break;
  }
};

export const ScreenCaptureButtons = () => {
  const iconButtonStyle = {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    margin: '0 5px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
    transition: 'transform 0.2s ease',
  };

  const activeIconButtonStyle = {
    ...iconButtonStyle,
    backgroundColor: '#ff4444',
    color: 'white',
  };

  const containerStyle = {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    padding: '10px',
    borderRadius: '12px',
  };

  const isRecording = mediaRecorder && mediaRecorder.state === "recording";

  return (
    <div style={containerStyle}>
      <button 
        style={iconButtonStyle}
        onClick={takeScreenshot}
        title="Take Screenshot"
      >
        <BsCameraFill size={20} />
      </button>

      <button 
        style={isRecording ? activeIconButtonStyle : iconButtonStyle}
        onClick={() => {
          if (!mediaRecorder || mediaRecorder.state === "inactive") {
            startRecording();
          } else {
            stopRecording();
          }
        }}
        title={isRecording ? "Stop Recording" : "Start Recording"}
      >
        {isRecording ? <MdStop size={20} /> : <MdFiberManualRecord size={20} />}
      </button>
    </div>
  );
};

export const FilterAndCaptureButtons = ({ selectedFilter, setSelectedFilter, getRendererInfo, mode = 'earrings' }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showJoystick, setShowJoystick] = useState(false);
  const defaultTarget = mode === 'necklace' ? 'necklace' : 'leftEarring';
  const [activeTarget, setActiveTarget] = useState(defaultTarget);

  const pills = ({
    earrings: [
      { id: 'leftEarring', label: 'L. Ear' },
      { id: 'rightEarring', label: 'R. Ear' },
    ],
    necklace: [],
    combo: [
      { id: 'leftEarring', label: 'L. Ear' },
      { id: 'rightEarring', label: 'R. Ear' },
      { id: 'necklace', label: 'Necklace' },
    ],
  })[mode] ?? [];

  // Update recording state when mediaRecorder state changes
  useEffect(() => {
    const checkRecordingState = () => {
      setIsRecording(mediaRecorder && mediaRecorder.state === "recording");
    };

    // Check initial state
    checkRecordingState();

    // Set up interval to check state
    const interval = setInterval(checkRecordingState, 500);
    return () => clearInterval(interval);
  }, []);

  const handleZoomIn = () => {
    updateZoomForTarget(getRendererInfo(), 1.1, activeTarget);
  };

  const handleZoomOut = () => {
    updateZoomForTarget(getRendererInfo(), 0.9, activeTarget);
  };

  const handleJoystickMove = (direction) => {
    updatePositionForTarget(getRendererInfo(), direction, activeTarget);
  };

  const close = () => { setIsOpen(false); setShowJoystick(false); };

  const openJoystick = () => {
    setShowJoystick(true);
    setIsOpen(false);
  };

  return (
    <>
      {/* Backdrop — tapping above any bar closes it */}
      {(isOpen || showJoystick) && <div className="fab-backdrop" onClick={close} />}

      {/* Settings bar — main controls */}
      {isOpen && !showJoystick && (
        <div className="settings-bar">
          {pills.length > 0 && (
            <div className="bar-pills">
              {pills.map(p => (
                <button
                  key={p.id}
                  onClick={() => setActiveTarget(p.id)}
                  className={`pill-button pill-button--small${activeTarget === p.id ? ' pill-button--active' : ''}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
          <div className="bar-controls">
            <div className="select-wrap">
              <select
                value={selectedFilter}
                onChange={e => setSelectedFilter(e.target.value)}
                className="filter-select filter-select--dark"
              >
                <option value="none">Filter</option>
                <option value="cartoon">Cartoon</option>
                <option value="animal">Animal</option>
              </select>
              <BiFilterAlt className="filter-icon filter-icon--dark" size={14} />
            </div>
            <button onClick={handleZoomIn} className="bar-button" title="Zoom In">
              <AiOutlineZoomIn size={20} />
            </button>
            <button onClick={handleZoomOut} className="bar-button" title="Zoom Out">
              <AiOutlineZoomOut size={20} />
            </button>
            <button onClick={openJoystick} className="bar-button" title="Move">
              <IoGameControllerOutline size={20} />
            </button>
            <button
              className="bar-button"
              onClick={() => {
                const info = getRendererInfo();
                takeScreenshot(info.mindarThree, info.poseMindarThree);
              }}
              title="Screenshot"
            >
              <BsCameraFill size={20} />
            </button>
            <button
              className={`bar-button${isRecording ? ' bar-button--recording' : ''}`}
              onClick={() => {
                if (!mediaRecorder || mediaRecorder.state === "inactive") {
                  const info = getRendererInfo();
                  startRecording(info.mindarThree, info.poseMindarThree);
                } else {
                  stopRecording();
                }
              }}
              title={isRecording ? "Stop" : "Record"}
            >
              {isRecording ? <MdStop size={20} /> : <MdFiberManualRecord size={20} />}
            </button>
            <button onClick={close} className="bar-button bar-button--close" title="Close">✕</button>
          </div>
        </div>
      )}

      {/* Move bar — replaces settings bar while positioning */}
      {showJoystick && (
        <div className="settings-bar">
          {pills.length > 0 && (
            <div className="bar-pills">
              {pills.map(p => (
                <button
                  key={p.id}
                  onClick={() => setActiveTarget(p.id)}
                  className={`pill-button pill-button--small${activeTarget === p.id ? ' pill-button--active' : ''}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
          <div className="bar-controls">
            <button onClick={() => handleJoystickMove('left')} className="bar-button">◀</button>
            <button onClick={() => handleJoystickMove('up')} className="bar-button">▲</button>
            <button onClick={() => handleJoystickMove('down')} className="bar-button">▼</button>
            <button onClick={() => handleJoystickMove('right')} className="bar-button">▶</button>
            <button onClick={() => setShowJoystick(false)} className="bar-button bar-button--done">✓</button>
          </div>
        </div>
      )}

      {/* FAB — only when both bars are closed */}
      {!isOpen && !showJoystick && (
        <div className="control-fab">
          <button className="fab-button" onClick={() => setIsOpen(true)} title="Controls">
            <IoSettingsOutline size={22} />
          </button>
        </div>
      )}
    </>
  );
};
